import { CommentType, STPageType, TextPropertyType } from '@tencent/slide-sdk-pb-model';
import { assert } from 'ts-essentials';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import { DataTypeUtils, PAGE_DATA_TYPES, DataType, SHAPE_DATA_TYPES, SLIDE_PAGE_DATA_TYPES } from '../../data';
import { TextPlaceholderType } from '../../constants/text-constants';
import { DeleteDirection } from '../../constants/table-constants';
import { checkTableRangeValid } from '../../utils/table-utils';
import {
    PageInsertPositionOutOfRangeError,
    PageNotFoundError,
    PageNotInPositionError,
    PagePositionOutOfRangeError,
    PageShapeTreeNotExistError,
    SectionBindingSlideIndexOutOfRangeError,
    SectionInsertPositionOutOfRangeError,
    SectionNotFoundError,
    ShapeInsertPositionOutOfRangeError,
    ShapeNotChildOfGroupShapeError,
    ShapeNotChildOfShapeTreeRootError,
    ShapeNotFoundError,
    ShapeNotInPageError,
    ShapeNotInPositionError,
    ShapePlaceholderNotExistError,
    ShapeTypeNotMatchError,
    ShapeInvalidTypeError,
    ThemeHasExistError,
    ThemeNotExistError,
    ThemeUsedError,
    TextPropertyTypeError,
    TextContentFieldError,
    TableAddRowOrColCountError,
    TableAddRowOrColIndexOutOfRangeError,
    TableDeleteRowOrColCountOutOfLengthError,
    TableDeleteRowOrColIndexOutOfRangeError,
    TableRangeExistNullError,
    TableRangeLessZeroError,
    TableRangeOutOfLengthError,
    SubChartNotFoundError,
    SubChartPositionOutOfRangeError,
    SubChartTypeNotMatchError,
    TableMergeAlreadyMergedCellError,
    TableUnMergeMultipleCellError,
    TableInsertRowInMergedCellError,
    TableInsertColInMergedCellError,
    TableDeleteRowInMergedCellError,
    TableDeleteColInMergedCellError,
    TableDeleteAllColOrRowError,
    ShapeListLengthTooShortError,
    ShapeRecursiveInsertionError,
    PageStaleReferenceError,
    ModelAlreadyExistError,
    ThemeFormatError,
    TableMergeSingleCellError,
    TableErrorCode,
    ResourceMapIdIsEmptyError,
    CommentGroupCpOfTextAnchorIsInvalidError,
    CommentGroupLenOfTextAnchorIsInvalidError,
    CommentGroupTextAnchorIsEmptyError,
    CommentGroupAnchorPositionIsInvalidError,
    CommentGroupCommentTypeIsEmptyError,
    CommentGroupIdIsEmptyError,
    CommentGroupShapeIdIsEmptyError,
    CommentGroupAnchorPositionIsEmptyError,
    CommentGroupTableRangeStartIndexIsInvalidError,
    CommentGroupTableRangeSpanIsInvalidError,
    CommentGroupIdIsRepeatedError,
    CommentGroupIsEmptyError,
    ExtensionHasPageIdError,
    ExtensionHasTargetIdError,
    ExtensionNoPageIdError,
    ExtensionNoTargetIdError,
    ExtensionDiffIdError,
    ModelNotFoundError,
} from './mutation-errors';
import type {
    ICTPoint2DDelta,
    ITableRangeData,
    ITextAnchor,
    ITextPropertyData,
    SubChartType,
} from '@tencent/slide-sdk-pb-model';
import type { MaybeError } from './base-mutation';
import type { IPresentationDataModel } from '../../model/presentation-data-model';
import type { IThemeData, ShapeDataType } from '../../data';

type RuleChecker = (...args: unknown[]) => MaybeError;

export const Rule = {
    every: (...checkers: (RuleChecker | MaybeError | false)[]): MaybeError => {
        for (const checker of checkers) {
            const error = typeof checker === 'function' ? checker() : checker;
            if (error) {
                return error;
            }
        }
    },

    forEach: <T>(items: T[], checker: (item: T) => MaybeError): MaybeError => {
        for (const item of items) {
            const error = checker(item);
            if (error) {
                return error;
            }
        }
    },

    model: {
        should: {
            notExist: (presentation: IPresentationDataModel, id: string): MaybeError => {
                const model = presentation.getModel(id);
                if (model) {
                    return ModelAlreadyExistError({ id, type: DataType[model.type] });
                }
            },
            exist: (presentation: IPresentationDataModel, id: string, ...dataType: DataType[]): MaybeError => {
                const model = presentation.getModel(id, ...dataType);
                if (!model) {
                    return ModelNotFoundError({ id });
                }
            },
        },
    },

    page: {
        should: {
            exist: (presentation: IPresentationDataModel, pageId: string, pageType?: STPageType): MaybeError => {
                const page = presentation.getModel(
                    pageId,
                    ...(pageType ? [DataTypeUtils.fromSTPageType(pageType)] : PAGE_DATA_TYPES),
                );

                if (!page) {
                    return PageNotFoundError({ pageId, pageType: pageType && STPageType[pageType] });
                }

                if (!page.store.spTree) {
                    return PageShapeTreeNotExistError({ pageId, pageType: STPageType[pageType ?? 0] });
                }
            },

            haveNoStaleReference: (presentation: IPresentationDataModel, pageId: string): MaybeError => {
                const page = presentation.getModel(pageId, ...PAGE_DATA_TYPES);
                if (!page) {
                    return PageNotFoundError({ pageId });
                }

                if (page.type === DataType.SlideLayout) {
                    const slide = presentation.slideList.find((slide) => slide.getLayout()?.id === pageId);
                    if (slide) {
                        return PageStaleReferenceError({ layout: page.id, refs: { slide: slide.id } });
                    }
                }

                if (page.type === DataType.SlideMaster) {
                    const layout = presentation.layoutList.find((layout) => layout.getMaster()?.id === pageId);
                    if (layout) {
                        return PageStaleReferenceError({ master: page.id, refs: { layout: layout.id } });
                    }
                }

                if (page.type === DataType.NotesMaster) {
                    const nodeSlideSize = presentation.noteSlideMap.size;
                    if (nodeSlideSize) {
                        return PageStaleReferenceError({ noteMaster: page.id, refs: { nodeSlideSize } });
                    }
                }
            },
        },

        can: {
            insertIntoMasterList: (
                presentation: IPresentationDataModel,
                insertMasterId: string,
                position: number,
            ): MaybeError => {
                const { length } = presentation.masterList;
                if (position > length) {
                    return PageInsertPositionOutOfRangeError({ insertMasterId, position, length });
                }
            },

            insertIntoLayoutIdList: (
                presentation: IPresentationDataModel,
                layoutIdToInsert: string,
                masterId: string,
                position: number,
            ): MaybeError => {
                const error = Rule.page.should.exist(presentation, masterId, STPageType.slideMaster);
                if (error) return error;

                const master = presentation.getModel(masterId, DataType.SlideMaster);
                assert(master, 'master should exist');

                const { length } = master.layoutList;
                if (position > length) {
                    return PageInsertPositionOutOfRangeError({
                        layoutIdToInsert,
                        masterId,
                        position,
                        length,
                    });
                }
            },

            insertIntoSlideList: (
                presentation: IPresentationDataModel,
                insertSlideId: string,
                position: number,
            ): MaybeError => {
                const { length } = presentation.slideList;
                if (position > length) {
                    return PageInsertPositionOutOfRangeError({ insertSlideId, position, length });
                }
            },
        },
    },

    pageList: {
        should: {
            containMasterInPosition(presentation: IPresentationDataModel, position: number, masterId?: string) {
                if (position < 0 || position >= presentation.masterList.length) {
                    return PagePositionOutOfRangeError({ masterId, position });
                }

                if (!masterId) {
                    return;
                }

                const error = Rule.page.should.exist(presentation, masterId, STPageType.slideMaster);
                if (error) return error;

                if (presentation.masterList[position].id !== masterId) {
                    return PageNotInPositionError({ masterId, position });
                }
            },

            containLayoutInPosition(
                presentation: IPresentationDataModel,
                masterId: string,
                position: number,
                layoutId?: string,
            ): MaybeError {
                let error = Rule.page.should.exist(presentation, masterId, STPageType.slideMaster);
                if (error) return error;

                const master = presentation.getModel(masterId, DataType.SlideMaster);
                assert(master, 'master should exist');

                if (position < 0 || position >= master.layoutList.length) {
                    return PagePositionOutOfRangeError({ layoutId, masterId, position });
                }

                if (!layoutId) {
                    return;
                }

                error = Rule.page.should.exist(presentation, layoutId, STPageType.slideLayout);
                if (error) return error;

                if (master.layoutList[position].id !== layoutId) {
                    return PageNotInPositionError({ layoutId, masterId, position });
                }
            },

            containSlideInPosition(
                presentation: IPresentationDataModel,
                position: number,
                slideId?: string,
            ): MaybeError {
                if (position < 0 || position >= presentation.slideList.length) {
                    return PagePositionOutOfRangeError({ slideId, position });
                }

                if (!slideId) {
                    return;
                }

                const error = Rule.page.should.exist(presentation, slideId, STPageType.slide);
                if (error) return error;

                if (presentation.slideList[position].id !== slideId) {
                    return PageNotInPositionError({ slideId, position });
                }
            },
        },
    },

    groupShape: {
        can: {
            insertShapeToPosition(
                presentation: IPresentationDataModel,
                groupShapeId: string,
                position: number,
                shapeIdToInsert?: string,
            ) {
                const groupShape = presentation.getModel(groupShapeId, DataType.GroupShape);
                if (!groupShape) {
                    return ShapeNotFoundError({ groupShapeId });
                }

                if (position < 0 || position > groupShape.shapeList.length) {
                    return ShapeInsertPositionOutOfRangeError({ groupShapeId, position, shapeIdToInsert });
                }

                if (shapeIdToInsert === groupShapeId) {
                    return ShapeRecursiveInsertionError({ groupShapeId, position, shapeIdToInsert });
                }
            },
        },
    },

    shape: {
        should: {
            existInPage: (presentation: IPresentationDataModel, shapeId: string, pageId: string): MaybeError => {
                const error = Rule.page.should.exist(presentation, pageId);
                if (error) return error;

                const shape = presentation.getModel(shapeId, ...SHAPE_DATA_TYPES);
                if (!shape) {
                    return ShapeNotFoundError({ shapeId });
                }

                if (shape.getPage().id !== pageId) {
                    return ShapeNotInPageError({ shapeId, pageId });
                }
            },

            hasPlaceholder: (presentation: IPresentationDataModel, shapeId: string): MaybeError => {
                const shape = presentation.getModel(shapeId, DataType.Shape);
                if (!shape) {
                    return ShapeNotFoundError({ shapeId, shapeType: DataType[DataType.Shape] });
                }

                const placeholder = shape.getRelatedPlaceholder();
                if (!placeholder) {
                    return ShapePlaceholderNotExistError({ shapeId });
                }
            },
            hasTextBody: (
                presentation: IPresentationDataModel,
                shapeId: string,
                col?: number,
                row?: number,
            ): MaybeError => {
                const shape = presentation.getModel(shapeId, DataType.Shape, DataType.GraphicalObjectFrameTable);
                if (!shape) {
                    return ShapeNotFoundError({ shapeId });
                }

                const shapeType = shape.props.type;
                if (
                    (shapeType === DataType.Shape && shape.props.txBody) ||
                    (shapeType === DataType.GraphicalObjectFrameTable &&
                        isNumber(col) &&
                        isNumber(row) &&
                        col > 0 &&
                        row > 0 &&
                        shape.props.graphic?.graphicData?.tbl?.tr?.[row]?.tc?.[col]?.txBody)
                )
                    return;
            },

            matchShapeListInPosition(
                presentation: IPresentationDataModel,
                groupShapeId: string,
                startIndex: number,
                childShapeIds: string[],
            ) {
                const groupShape = presentation.getModel(groupShapeId, DataType.GroupShape);
                if (!groupShape) {
                    return ShapeNotFoundError({ groupShapeId });
                }

                const shapeIds = groupShape.store.shapeList.toArray().map((shape) => shape.id);
                if (startIndex + childShapeIds.length > shapeIds.length) {
                    return ShapeListLengthTooShortError({ groupShapeId, index: startIndex, childShapeIds });
                }

                for (let i = 0; i < childShapeIds.length; i++) {
                    if (childShapeIds[i] !== shapeIds[startIndex + i]) {
                        return ShapeNotInPositionError({
                            childShapeIds,
                            shapeIds,
                            groupShapeId,
                            startIndex,
                        });
                    }
                }
            },
        },
        is: {
            childOfShapeTreeRoot: (
                presentation: IPresentationDataModel,
                shapeId: string,
                pageId: string,
                index?: number,
            ): MaybeError => {
                const error = Rule.shape.should.existInPage(presentation, shapeId, pageId);
                if (error) {
                    return error;
                }

                const shape = presentation.getModel(shapeId, ...SHAPE_DATA_TYPES);
                const page = presentation.getModel(pageId, ...SLIDE_PAGE_DATA_TYPES);

                if (shape?.parent !== page?.store.getShapeTree()) {
                    return ShapeNotChildOfShapeTreeRootError({ shapeId, pageId });
                }

                if (
                    index !== undefined &&
                    page?.store.getShapeTree().store.shapeList.findIndexById(shapeId) !== index
                ) {
                    return ShapeNotInPositionError({ shapeId, pageId, index });
                }
            },

            childOfGroupShape: (
                presentation: IPresentationDataModel,
                shapeId: string,
                groupShapeId?: string,
            ): MaybeError => {
                const shape = presentation.getModel(shapeId, ...SHAPE_DATA_TYPES);
                if (!shape) {
                    return ShapeNotFoundError({ shapeId });
                }

                if (groupShapeId !== undefined) {
                    const groupShape = presentation.getModel(groupShapeId, DataType.GroupShape);
                    if (!groupShape) {
                        return ShapeNotFoundError({ groupShapeId });
                    }

                    if (!groupShape.store.shapeList.findById(shapeId)) {
                        return ShapeNotChildOfGroupShapeError({ shapeId, groupShapeId });
                    }
                } else {
                    if (shape.parent.type !== DataType.GroupShape) {
                        return ShapeNotChildOfGroupShapeError({ shapeId, parentType: shape.parent.type });
                    }
                }
            },

            typeOf: (presentation: IPresentationDataModel, shapeId: string, shapeType: ShapeDataType): MaybeError => {
                const shape = presentation.getModel(shapeId, ...SHAPE_DATA_TYPES);
                if (shape?.type !== shapeType) {
                    return ShapeTypeNotMatchError({ shapeId, shapeType });
                }
            },
            notTypeOf: (
                presentation: IPresentationDataModel,
                shapeId: string,
                ...shapeTypes: ShapeDataType[]
            ): MaybeError => {
                const shape = presentation.getModel(shapeId, ...SHAPE_DATA_TYPES);
                for (const shapeType of shapeTypes) {
                    if (shape?.type === shapeType) {
                        return ShapeInvalidTypeError({ shapeId, shapeType });
                    }
                }
            },
        },
    },

    table: {
        range: {
            is: {
                noMergedRange: (
                    presentation: IPresentationDataModel,
                    shapeId: string,
                    range: Required<ITableRangeData>,
                ): MaybeError => {
                    const table = presentation.getModel(shapeId, DataType.GraphicalObjectFrameTable);
                    if (!table) {
                        return ShapeNotFoundError({ shapeId });
                    }
                    const { startRowIndex, startColIndex, rowSpan, colSpan } = range;
                    if (rowSpan === 1 && colSpan === 1) {
                        return TableMergeSingleCellError({ startRowIndex, startColIndex });
                    }
                    for (let row = startRowIndex; row < startRowIndex + rowSpan; row += 1) {
                        for (let col = startColIndex; col < startColIndex + colSpan; col += 1) {
                            const cell = table.store.data.graphic?.graphicData?.tbl?.tr?.[row]?.tc?.[col];
                            if (cell?.hMerge || cell?.vMerge || cell?.rowSpan || cell?.gridSpan) {
                                return TableMergeAlreadyMergedCellError({ shapeId, range });
                            }
                        }
                    }
                },
                singleMergedRange: (
                    presentation: IPresentationDataModel,
                    shapeId: string,
                    range: Required<ITableRangeData>,
                ): MaybeError => {
                    const table = presentation.getModel(shapeId, DataType.GraphicalObjectFrameTable);
                    if (!table) {
                        return ShapeNotFoundError({ shapeId });
                    }
                    const { startRowIndex, startColIndex, rowSpan, colSpan } = range;
                    const firstCell =
                        table.store.data.graphic?.graphicData?.tbl?.tr?.[startRowIndex]?.tc?.[startColIndex];
                    if ((firstCell?.rowSpan ?? 1) !== rowSpan || (firstCell?.gridSpan ?? 1) !== colSpan) {
                        return TableUnMergeMultipleCellError({ shapeId, range });
                    }
                },
            },
        },
        can: {
            // eslint-disable-next-line complexity
            addRowOrCol: (
                presentation: IPresentationDataModel,
                shapeId: string,
                index: number,
                count: number,
                isRow: boolean,
                mergedRanges: Required<ITableRangeData>[],
            ): MaybeError => {
                const table = presentation.getModel(shapeId, DataType.GraphicalObjectFrameTable);
                if (!table) {
                    return ShapeNotFoundError({ shapeId });
                }
                if (count <= 0) {
                    return TableAddRowOrColCountError({ shapeId });
                }
                const length = isRow ? table.rowLength : table.colLength;
                if (index < 0 || index > length) {
                    return TableAddRowOrColIndexOutOfRangeError({ index, length });
                }
                for (const range of mergedRanges) {
                    const startIndex = isRow ? range.startRowIndex : range.startColIndex;
                    const spanNumber = isRow ? range.rowSpan : range.colSpan;
                    if (index > startIndex && index < startIndex + spanNumber) {
                        return isRow
                            ? TableInsertRowInMergedCellError({ shapeId, index, count })
                            : TableInsertColInMergedCellError({ shapeId, index, count });
                    }
                }
            },

            // eslint-disable-next-line complexity
            deleteRowOrCol: (
                presentation: IPresentationDataModel,
                shapeId: string,
                index: number,
                count: number,
                deleteDirection: DeleteDirection,
                mergedRanges: Required<ITableRangeData>[],
            ): MaybeError => {
                const table = presentation.getModel(shapeId, DataType.GraphicalObjectFrameTable);
                if (!table) {
                    return ShapeNotFoundError({ shapeId });
                }
                const length = deleteDirection ? table.colLength : table.rowLength;
                const type = deleteDirection ? 'col' : 'row';
                if (count >= length) {
                    return TableDeleteAllColOrRowError({ index, length });
                }
                if (count <= 0 || index + count > length) {
                    return TableDeleteRowOrColCountOutOfLengthError({
                        shapeId,
                        delete: { [type]: { index, count } },
                        length,
                    });
                }
                if (index < 0 || index > length) {
                    return TableDeleteRowOrColIndexOutOfRangeError({
                        shapeId,
                        delete: { [type]: { index, count } },
                        length,
                    });
                }
                for (const range of mergedRanges) {
                    const isRow = deleteDirection === DeleteDirection.ROW;
                    const startIndex = isRow ? range.startRowIndex : range.startColIndex;
                    const spanNumber = isRow ? range.rowSpan : range.colSpan;
                    if (index + count > startIndex && index < startIndex + spanNumber) {
                        return isRow
                            ? TableDeleteRowInMergedCellError({ shapeId, index, count })
                            : TableDeleteColInMergedCellError({ shapeId, index, count });
                    }
                }
            },

            mergeTableCells: (
                presentation: IPresentationDataModel,
                shapeId: string,
                range: ITableRangeData,
            ): MaybeError => {
                const table = presentation.getModel(shapeId, DataType.GraphicalObjectFrameTable);
                if (!table) {
                    return ShapeNotFoundError({ shapeId });
                }
                const errCode = checkTableRangeValid(table.rowLength, table.colLength, range);
                if (errCode === TableErrorCode.TableRangeExistNullError) {
                    return TableRangeExistNullError({ shapeId });
                }
                if (errCode === TableErrorCode.TableRangeLessZeroError) {
                    return TableRangeLessZeroError({ shapeId });
                }
                if (errCode === TableErrorCode.TableRangeOutOfLengthError) {
                    return TableRangeOutOfLengthError({ shapeId });
                }
            },

            setTableCells: (
                presentation: IPresentationDataModel,
                shapeId: string,
                range: ITableRangeData,
            ): MaybeError => {
                const table = presentation.getModel(shapeId, DataType.GraphicalObjectFrameTable);
                if (!table) {
                    return ShapeNotFoundError({ shapeId });
                }
                const { startRowIndex, startColIndex, rowSpan, colSpan } = range;
                if (isNil(startRowIndex) || isNil(startColIndex) || !rowSpan || !colSpan) {
                    return TableRangeExistNullError({ shapeId });
                }
                if (Math.min(colSpan, rowSpan) <= 0) {
                    return TableRangeLessZeroError({ shapeId });
                }
                if (startRowIndex + rowSpan > table.rowLength || startColIndex + colSpan > table.colLength) {
                    return TableRangeOutOfLengthError({ shapeId });
                }
            },
        },
    },

    section: {
        can: {
            insertIntoGapPosition: (
                presentation: IPresentationDataModel,
                sectionName: string,
                position?: number,
            ): MaybeError => {
                if (position === undefined) return;

                const totalLength = presentation.slideList.length + presentation.sectionList.length;
                if (position < 0 || position > totalLength) {
                    return SectionInsertPositionOutOfRangeError({ position, sectionName, totalLength });
                }
            },
            bindSlide: (presentation: IPresentationDataModel, slideIndex: number): MaybeError => {
                const { length } = presentation.slideList;
                if (slideIndex < 0 || slideIndex > length) {
                    return SectionBindingSlideIndexOutOfRangeError({ slideIndex, length });
                }
            },
        },
        should: {
            exist: (presentation: IPresentationDataModel, sectionId: string): MaybeError => {
                const section = presentation.getModel(sectionId, DataType.Section);
                if (!section) {
                    return SectionNotFoundError({ sectionId });
                }
            },
        },
    },

    chart: {
        should: {
            subChartExist: (
                presentation: IPresentationDataModel,
                pageId: string,
                shapeId: string,
                position: number,
            ) => {
                const error = Rule.page.should.exist(presentation, pageId);
                if (error) return error;

                const chart = presentation.getModel(shapeId, DataType.GraphicalObjectFrameChart);
                assert(chart, 'chart should exist');

                const subChart = chart.getSubChart(position);

                if (!subChart) {
                    return SubChartNotFoundError({ shapeId, pageId, position });
                }
            },
        },
        can: {},
        is: {
            inSideSubChartRange: (
                presentation: IPresentationDataModel,
                pageId: string,
                shapeId: string,
                position: number,
            ) => {
                const error = Rule.page.should.exist(presentation, pageId);
                if (error) return error;

                const chart = presentation.getModel(shapeId, DataType.GraphicalObjectFrameChart);
                assert(chart, 'chart should exist');

                if (position < 0 || position > chart.store.subChartList.length) {
                    return SubChartPositionOutOfRangeError({ shapeId, pageId, position });
                }
            },

            matchedSubChartType: (
                presentation: IPresentationDataModel,
                pageId: string,
                shapeId: string,
                position: number,
                type?: SubChartType,
            ) => {
                const error = Rule.chart.should.subChartExist(presentation, pageId, shapeId, position);
                if (error) {
                    return error;
                }

                const chart = presentation.getModel(shapeId, DataType.GraphicalObjectFrameChart);
                const modelSubChartType = chart?.getSubChart(position).type;

                if (chart?.getSubChart(position).type !== type || !type) {
                    return SubChartTypeNotMatchError({
                        shapeId,
                        pageId,
                        position,
                        modelSubChartType,
                        mutationSubChartType: type,
                    });
                }
            },
        },
    },

    theme: {
        should: {
            existInPresentation: (presentation: IPresentationDataModel, themeId: string): MaybeError => {
                const themeModel = presentation.getThemeById(themeId);

                if (!themeModel) {
                    return ThemeNotExistError({ themeId, pageType: DataType.Theme });
                }
            },
            notUsedInMaster: (presentation: IPresentationDataModel, themeId: string): MaybeError => {
                const useInMaster = presentation.masterList.some((master) => master.getTheme()?.id === themeId);

                if (useInMaster) {
                    return ThemeUsedError({ themeId, pageType: DataType.Theme });
                }
            },
            // TODO: 主题的格式规则比较多，后续需要补充
            // eslint-disable-next-line complexity
            hasCorrectFormat: (themeData: IThemeData) => {
                const themeId = themeData.id;
                const { fontScheme, fmtScheme } = themeData.themeElements ?? {};
                if (fontScheme) {
                    const { majorFont, minorFont } = fontScheme;
                    if (!majorFont || !minorFont) {
                        return ThemeFormatError({ themeId, pageType: DataType.Theme, fontScheme });
                    }
                    if (
                        !majorFont.cs ||
                        !majorFont.ea ||
                        !majorFont.latin ||
                        !minorFont.cs ||
                        !minorFont.ea ||
                        !minorFont.latin
                    ) {
                        return ThemeFormatError({ themeId, pageType: DataType.Theme, fontScheme });
                    }
                }
                if (fmtScheme) {
                    const { bgFillStyleLst, fillStyleLst, lnStyleLst, effectStyleLst } = fmtScheme;
                    if (!bgFillStyleLst || !fillStyleLst || !lnStyleLst || !effectStyleLst) {
                        return ThemeFormatError({ themeId, pageType: DataType.Theme, fmtScheme });
                    }
                }
            },
        },
        can: {
            insertIntoThemeList: (presentation: IPresentationDataModel, themeId: string): MaybeError => {
                const themeModel = presentation.getThemeById(themeId);

                if (themeModel) {
                    return ThemeHasExistError({ themeId, pageType: DataType.Theme });
                }
            },
        },
    },

    text: {
        textContent: {
            is: {
                valid: (textContent: string): MaybeError => {
                    // 通过stack检查 field 的属性：由于 slides 中 field 属性由 kFieldBegin 和 kFieldEnd
                    // 成对组成，所以如果出现缺失或嵌套的场景均为不合法
                    const fieldStack: string[] = [];

                    // 遍历 text ，尽量在 O(n) 时间内处理
                    for (const character of textContent) {
                        if (
                            character === TextPlaceholderType.kFieldBegin ||
                            character === TextPlaceholderType.kFieldEnd
                        ) {
                            const err = Rule.text.textContent.isValidField(fieldStack, character);
                            if (err) return err;
                        }
                    }
                },
            },
            isValidField: (fieldStack: string[], character: string): MaybeError => {
                switch (character) {
                    case TextPlaceholderType.kFieldBegin: {
                        if (!fieldStack.length) {
                            // 为空则正常 push
                            fieldStack.push(TextPlaceholderType.kFieldBegin);
                            return;
                        }
                        return TextContentFieldError({
                            msg: 'fieldStack should be empty before kFieldBegin',
                            fieldStack,
                        });
                    }
                    case TextPlaceholderType.kFieldEnd: {
                        // 遇到“结束符”时唯一合法的场景是 stack 中顶部是“开始符”，说明配对成功
                        // 此时以配对成功可以抵消，因此 pop “开始符”
                        if (fieldStack.length && fieldStack[0] === TextPlaceholderType.kFieldBegin) {
                            fieldStack.pop();
                            return;
                        }
                        // 其它场景均不合法
                        return TextContentFieldError({
                            msg: '1.fieldStack should not be empty after kFieldEnd 2.should have kFieldStart before kFieldEnd',
                        });
                    }
                    default:
                        return TextContentFieldError({ msg: 'field character is not matched', character });
                }
            },
        },
        textProperties: {
            should: {
                valid: (textProperties: ITextPropertyData[], text: string): MaybeError => {
                    for (const textProperty of textProperties) {
                        switch (textProperty.textPropertyType) {
                            case TextPropertyType.Run: {
                                const err = Rule.text.textProperties.run.is.valid(textProperty, text);
                                if (err) return err;
                                break;
                            }
                            case TextPropertyType.Paragraph: {
                                const err = Rule.text.textProperties.paragraph.is.valid(textProperty, text);
                                if (err) return err;
                                break;
                            }
                            case TextPropertyType.Field: {
                                const err = Rule.text.textProperties.field.is.valid(textProperty, text);
                                if (err) return err;
                                break;
                            }
                            default:
                                return TextPropertyTypeError({
                                    msg: 'textPropertyType is not matched',
                                    textPropertyType: textProperty.textPropertyType,
                                });
                        }
                    }
                },
            },
            run: {
                is: {
                    valid: ({ runProperties, count = 0, index = 0 }: ITextPropertyData, text: string): MaybeError => {
                        const curText = text[index + count - 1];
                        // 当 isPlaceholder 为 true 时，当且仅当修改范围 count 为1，且该字符为“硬换行符/域开始/域结束”时是合理的，其它所有情况均不合理
                        if (runProperties?.isPlaceholder) {
                            if (
                                count === 1 &&
                                (curText === TextPlaceholderType.kParagraphReturn ||
                                    curText === TextPlaceholderType.kFieldBegin ||
                                    curText === TextPlaceholderType.kFieldEnd)
                            ) {
                                return;
                            }
                            return TextPropertyTypeError({
                                count,
                                index,
                                curText,
                                msg: 'endParaRPr character should be kParagraphReturn',
                            });
                        }
                    },
                },
            },
            paragraph: {
                is: {
                    valid: ({ count = 0, index = 0 }: ITextPropertyData, text: string): MaybeError => {
                        // 当设置段属性时，需要确保最后的一个字符时“硬换行符”
                        if (text[index + count - 1] === TextPlaceholderType.kParagraphReturn) {
                            return;
                        }
                        return TextPropertyTypeError({
                            index,
                            count,
                            curText: text[index + count - 1],
                            msg: 'curText should be kParagraphReturn',
                        });
                    },
                },
            },
            field: {
                is: {
                    valid: ({ count = 0, index = 0 }: ITextPropertyData, text: string): MaybeError => {
                        // 由于 field 属性有开始和结束占位符，需要进行判断, 同时需要判断修改的范围内
                        // 请参考：docx-online library/collab/core/doc/document/placeholder.h:41
                        if (
                            text[index] === TextPlaceholderType.kFieldBegin ||
                            text[index + count - 1] === TextPlaceholderType.kFieldEnd
                        ) {
                            return;
                        }
                        return TextPropertyTypeError({
                            fieldStart: text[index],
                            fieldEnd: text[index + count - 1],
                        });
                    },
                },
            },
        },
    },

    resourceMap: {
        id: {
            is: {
                valid: (id: string): MaybeError => {
                    return id === '' ? ResourceMapIdIsEmptyError({}) : undefined;
                },
            },
        },
    },

    comment: {
        commentType: {
            should: {
                exist: (commentType: CommentType) => {
                    if (!commentType) {
                        return CommentGroupCommentTypeIsEmptyError({});
                    }
                },
            },
        },
        groupId: {
            should: {
                exist: (presentation: IPresentationDataModel, pageId: string, groupId: string) => {
                    if (!groupId) return CommentGroupIdIsEmptyError({});
                    const store = presentation.getModel(pageId, DataType.Slide)?.store;
                    const commentGroupMap = store?.data.commentGroupMap;
                    if (!commentGroupMap?.[groupId]) return CommentGroupIsEmptyError({});
                },
                notExist: (presentation: IPresentationDataModel, pageId: string, groupId: string) => {
                    if (!groupId) return CommentGroupIdIsEmptyError({});
                    const store = presentation.getModel(pageId, DataType.Slide)?.store;
                    const commentGroupMap = store?.data.commentGroupMap;
                    if (commentGroupMap?.[groupId]) return CommentGroupIdIsRepeatedError({});
                },
            },
        },
        shapeId: {
            is: {
                valid: (commentType: CommentType, shapeId?: string): MaybeError => {
                    if (commentType === CommentType.element || commentType === CommentType.text) {
                        if (!shapeId) return CommentGroupShapeIdIsEmptyError({});
                    }
                },
            },
        },
        anchorPosition: {
            is: {
                valid: (anchorPosition?: ICTPoint2DDelta): MaybeError => {
                    if (anchorPosition) {
                        if (typeof anchorPosition.x !== 'number' || typeof anchorPosition.y !== 'number')
                            return CommentGroupAnchorPositionIsInvalidError({});
                        if (anchorPosition.x < 0 || anchorPosition.y < 0)
                            return CommentGroupAnchorPositionIsInvalidError({});
                    }
                },
            },
            should: {
                exist: (commentType: CommentType, anchorPosition?: ICTPoint2DDelta): MaybeError => {
                    if (commentType === CommentType.text) {
                        if (!anchorPosition) return CommentGroupAnchorPositionIsEmptyError({});
                    }
                },
            },
        },
        textAnchor: {
            should: {
                exist: (commentType: CommentType, textAnchor?: ITextAnchor) => {
                    if (commentType === CommentType.text) {
                        if (!textAnchor) return CommentGroupTextAnchorIsEmptyError({});
                    }
                },
            },
            is: {
                valid: (textAnchor?: ITextAnchor): MaybeError => {
                    if (typeof textAnchor?.cp !== 'undefined') {
                        if (textAnchor?.cp < 0) return CommentGroupCpOfTextAnchorIsInvalidError({});
                    }
                    if (typeof textAnchor?.len !== 'undefined') {
                        if (textAnchor.len < 0) return CommentGroupLenOfTextAnchorIsInvalidError({});
                    }
                    if (typeof textAnchor?.commentTableRange !== 'undefined') {
                        const {
                            startColIndex = 0,
                            startRowIndex = 0,
                            colSpan = 1,
                            rowSpan = 1,
                        } = textAnchor.commentTableRange;
                        if (startColIndex < 0 || startRowIndex < 0)
                            return CommentGroupTableRangeStartIndexIsInvalidError({});
                        if (colSpan < 1 || rowSpan < 1) return CommentGroupTableRangeSpanIsInvalidError({});
                    }
                },
            },
        },
    },

    extensions: {
        should: {
            hasPageId: (pageId?: string) => (!pageId ? ExtensionNoPageIdError({}) : undefined),
            hasTargetId: (targetId?: string) => (!targetId ? ExtensionNoTargetIdError({}) : undefined),
            notHasPageId: (pageId?: string) => (pageId ? ExtensionHasPageIdError({ pageId }) : undefined),
            notHasTargetId: (targetId?: string) => (targetId ? ExtensionHasTargetIdError({ targetId }) : undefined),
            equalTargetIdAndPageId: (targetId?: string, pageId?: string) =>
                targetId !== pageId ? ExtensionDiffIdError({ pageId, targetId }) : undefined,
        },
    },
} as const;
